/**
 * Component for the `Notification details`/`EziDebit payment options` panel
 */

import { Button, Col, Drawer, Form, Modal, Row } from 'antd';
import { includes, map } from 'lodash';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { withNumberFormatHandler } from '../../components/common/NumberFormatHandler';
import { Invoice } from '../../store/invoices/types';
import { getIsMobile } from '../../utils/commonFunctions';

interface IProps {
    readonly visible: boolean;
    readonly closePanel?: (refetchList?: boolean) => void;
    formatCurrency: (amount: number) => JSX.Element;
    readonly totalPayment: number;
    readonly invoices: Invoice[];
    readonly selectedRowKeys: any[];
    readonly onPaymentOptionChoose: (
        totalPayment: number,
        payByCard: boolean
    ) => void;
    readonly isConfirmation: boolean;
}

const NotificationEziDebitPaymentOptionsPanel: React.FC<IProps> = ({
    visible,
    closePanel,
    formatCurrency,
    totalPayment,
    invoices,
    selectedRowKeys,
    onPaymentOptionChoose,
    isConfirmation,
}: IProps) => {
    const isMobile = getIsMobile();
    const panelTitle = 'Payment options';

    /**
     * Function called when drawer or modal is closed.
     */
    const handleClosePanel = () => {
        if (closePanel) closePanel();
    };

    /**
     * Function when pay button is clicked on either by Credit card or by bank.
     * @param payByCreditCard
     */
    const onPayButtonClick = (payByCreditCard: boolean) => {
        return () => {
            // const prefixText = payByCreditCard ? 'Overseas' : '';
            // const cardPaymentSelected = get(
            //     paymentOptionsData,
            //     paymentOptionsDataMapping[`${prefixText}CardPaymentSystem`]
            // );

            // if (cardPaymentSelected === cardPaymentSystems.SELF_MANAGED) {

            // } else if (
            //     includes(paymentGatewaysWithRedirect, cardPaymentSelected)
            // ) {
            onPaymentOptionChoose(totalPayment, payByCreditCard);
            // }
        };
    };

    const populateInvoicesList = () => {
        return map(invoices, (invoice: Invoice) => {
            if (includes(selectedRowKeys, invoice.Id)) {
                return <div key={invoice.Id} style={{ whiteSpace: 'pre' }}>{invoice.Number}</div>;
            }
        });
    };

    /**
     * Function for populating the panel content (with loading skeletons/data).
     */
    const populatePanelContent = () => {
        return (
            <Row>
                <Col>
                    <Row>
                        <Col>
                            {isConfirmation
                                ? 'Please confirm your payment of'
                                : 'You are making a payment of'}{' '}
                            {formatCurrency(totalPayment)} for the following
                            invoices:
                        </Col>
                        <Col className="mb-10 mt-10">
                            {populateInvoicesList()}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {isConfirmation
                                ? 'via bank transfer:'
                                : 'Please select one of these two payment options:'}
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    };

    /**
     * Function that populates pay by credit card or pay by bank buttons
     */
    const populatePayButtons = () => {
        const colSpan = isMobile ? 24 : 12;
        if (isConfirmation) {
            return (
                <Row gutter={10}>
                    <Col span={colSpan} className={isMobile ? 'mb-5' : ''}>
                        <Button
                            type="primary"
                            block
                            onClick={onPayButtonClick(false)}
                        >
                            Proceed with Payment
                        </Button>
                    </Col>
                </Row>
            );
        } else {
            return (
                <Row gutter={10}>
                    <Col span={colSpan} className={isMobile ? 'mb-5' : ''}>
                        <Button
                            type="primary"
                            block
                            onClick={onPayButtonClick(true)}
                        >
                            Pay by Credit Card
                        </Button>
                    </Col>
                    <Col span={colSpan}>
                        <Button
                            type="primary"
                            block
                            onClick={onPayButtonClick(false)}
                        >
                            Pay by Bank Account
                        </Button>
                    </Col>
                </Row>
            );
        }
    };

    /**
     * Common function that populates the cancel button section.
     */
    const populateButtonsSection = () => {
        return (
            <Row type="flex">
                {!isMobile && (
                    <Col className="mr-20 fx-1">{populatePayButtons()}</Col>
                )}
                <Col className="ta-right">
                    <Button onClick={handleClosePanel}>Cancel</Button>
                </Col>
            </Row>
        );
    };

    return (
        <Row>
            <Col>
                {isMobile ? (
                    <div>
                        <Modal
                            className="modal-swapped-buttons modal-no-padding"
                            title={panelTitle}
                            visible={visible}
                            footer={populateButtonsSection()}
                            style={{ top: 12 }}
                        >
                            {visible && (
                                <div
                                    style={{
                                        maxHeight: window.innerHeight - 160,
                                        overflowY: 'auto',
                                        padding: 24,
                                    }}
                                >
                                    {populatePanelContent()}
                                    <div className="mt-10">
                                        {populatePayButtons()}
                                    </div>
                                </div>
                            )}
                        </Modal>
                    </div>
                ) : (
                    <Drawer
                        title={panelTitle}
                        width={500}
                        onClose={handleClosePanel}
                        visible={visible}
                        className="notification-payment-options-panel"
                        closable={false}
                        maskClosable={false}
                    >
                        {visible && (
                            <>
                                <div className="mb-8">
                                    {populatePanelContent()}
                                </div>
                                {populateButtonsSection()}
                            </>
                        )}
                    </Drawer>
                )}
            </Col>
        </Row>
    );
};

const NotificationEziDebitPaymentOptionsPanelForm = Form.create({
    name: 'notification-ezidebit-payment-options-panel-form',
})(NotificationEziDebitPaymentOptionsPanel);

export default withRouter(
    withNumberFormatHandler(NotificationEziDebitPaymentOptionsPanelForm)
);
